// Self-heal for an expired/invalid session. Kept dependency-free (no axios) so
// it can be unit-tested in isolation and reused by the axios interceptor.

// Auth endpoints manage their own 401s (a failed login/register, or the Navbar's
// /me probe for logged-out visitors) — never treat those as an expired session.
const AUTH_ENDPOINT = /\/api\/auth\//

// Whether a 401 should trigger a silent token refresh + retry. Only for an
// authed endpoint, when we believed we were logged in, and not already retried
// once (the interceptor sets `_retried` before retrying to avoid a loop).
export function shouldAttemptRefresh(error) {
  if (error?.response?.status !== 401) return false
  if (AUTH_ENDPOINT.test(error?.config?.url || '')) return false
  if (error?.config?._retried) return false
  if (!localStorage.getItem('user')) return false
  return true
}

// When an authenticated request 401s, the session cookie is dead. The client,
// however, still has `localStorage.user`, so without this the UI stays "logged
// in" and every authed call fails until a manual logout+login. Self-heal: drop
// the stale user and bounce to login (preserving where they were). Guarded so it
// only fires when we believed we were authenticated — guests browsing public
// pages that 401 are unaffected.
//
// This is the FALLBACK for when a silent token refresh also fails; the axios
// interceptor attempts /api/auth/refresh first.
export function handleExpiredSession(error) {
  if (error?.response?.status !== 401) return
  if (AUTH_ENDPOINT.test(error?.config?.url || '')) return
  if (!localStorage.getItem('user')) return

  localStorage.removeItem('user')

  const { pathname, search } = window.location
  if (pathname === '/login' || pathname === '/login/') return
  const next = encodeURIComponent(pathname + search)
  window.location.href = `/login?next=${next}&session=expired`
}
